import { Box, Button, Checkbox, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Radio, RadioGroup, Select, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useMemo, useState } from "react";
import { adminDeleteReplies, adminGetReplies, adminUpdateReplyHiddenReason, adminUpdateReplyStatus } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import TextModal from "../../../components/Modals/TextModal";
import { colors } from "../../../styles";
import { IAdminReply, IAdminReplyList } from "../../../types";
import { formatDateTimeKor } from "../../../utils";

export default function Replies() {

    const [page, setPage] = useState(1);
    const size = 100;
    const [isReportedOnly, setIsReportedOnly] = useState<string>("all");
    const [isHiddenOnly, setIsHiddenOnly] = useState<string>("all");

    const reportedOnlyParam = useMemo(() => {
        if (isReportedOnly === "all") return undefined;
        return isReportedOnly === "true";
    }, [isReportedOnly]);

    const hiddenOnlyParam = useMemo(() => {
        if (isHiddenOnly === "all") return undefined;
        return isHiddenOnly === "true";
    }, [isHiddenOnly]);

    const handleIsReportedOnlyChange = (value: string) => {
        setPage(1);
        setIsReportedOnly(value);
    };

    const handleIsHiddenOnlyChange = (value: string) => {
        setPage(1);
        setIsHiddenOnly(value);
    };

    const { data: replyListData, isLoading: isReplyListDataLoading } = useQuery<IAdminReplyList>({
        queryKey: ['adminGetReplies', page, size, reportedOnlyParam, hiddenOnlyParam],
        queryFn: () => adminGetReplies({ page, size, isReportedOnly: reportedOnlyParam, isHiddenOnly: hiddenOnlyParam }),
        enabled: true,
    });

    const totalPages = useMemo(() => replyListData?.totalPages ?? 0, [replyListData]);
    const currentPage = useMemo(() => replyListData?.currentPage ?? 0, [replyListData]);

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    const replyColumns = [
        { key: "comment.chat.profile.nickname", label: "📄👤 원글 작성자" },
        { key: "comment.chat.content", label: "📄 원글 내용" },
        { key: "comment.profile.nickname", label: "💬👤 원댓글 작성자" },
        { key: "comment.content", label: "💬 원댓글 내용" },
        { key: "profile.nickname", label: "💬💬👤 답글 작성자" },
        { key: "content", label: "💬💬 답글 내용" },
        { key: "reports", label: "신고 수" },
        { key: "status", label: "상태" },
        { key: "hiddenReason", label: "숨김 사유" },
        { key: "createdAt", label: "생성 일자" },
        { key: "updatedAt", label: "수정 일자" },
    ];

    const [selectedReplyIds, setSelectedReplyIds] = useState<string[]>([]);
    const currentPageReplyIds = useMemo(() => replyListData?.replies.map((reply) => reply.id) ?? [], [replyListData]);

    const isAllCurrentPageSelected = useMemo(() => {
        return currentPageReplyIds.length > 0 && currentPageReplyIds.every((id) => selectedReplyIds.includes(id));
    }, [currentPageReplyIds, selectedReplyIds]);

    const isSomeCurrentPageSelected = useMemo(() => {
        const someSelected = currentPageReplyIds.some((id) => selectedReplyIds.includes(id));
        return someSelected && !isAllCurrentPageSelected;
    }, [currentPageReplyIds, selectedReplyIds, isAllCurrentPageSelected]);

    const toggleSelectAllCurrentPage = () => {
        if (isAllCurrentPageSelected) {
            setSelectedReplyIds((prev) => prev.filter((id) => !currentPageReplyIds.includes(id)));
        } else {
            setSelectedReplyIds((prev) => Array.from(new Set([...prev, ...currentPageReplyIds])));
        }
    };

    const toggleSelectOne = (replyId: string) => {
        setSelectedReplyIds((prev) => (prev.includes(replyId) ? prev.filter((id) => id !== replyId) : [...prev, replyId]));
    };

    const { isOpen: isDeleteModalOpen, onOpen: onOpenDeleteModal, onClose: onCloseDeleteModal } = useDisclosure();

    const queryClient = useQueryClient();
    const toast = useToast();

    const deleteRepliesMutation = useMutation({
        mutationFn: adminDeleteReplies,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetReplies'] });
            setSelectedReplyIds([]);
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "답글 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const onDeleteReplies = () => {
        deleteRepliesMutation.mutate({ replyIds: selectedReplyIds });
        onCloseDeleteModal();
    };

    const { isOpen: isTextModalOpen, onOpen: onOpenTextModal, onClose: onCloseTextModal } = useDisclosure();
    const [selectedText, setSelectedText] = useState<string>("");

    const openText = (text: string) => {
        setSelectedText(text);
        onOpenTextModal();
    };

    const closeText = () => {
        setSelectedText("");
        onCloseTextModal();
    };

    const updateReplyStatusMutation = useMutation({
        mutationFn: adminUpdateReplyStatus,
        onMutate: async (variables) => {
            const { replyId, status } = variables;
            await queryClient.cancelQueries({ queryKey: ['adminGetReplies', page, size, reportedOnlyParam, hiddenOnlyParam] });
            const previous = queryClient.getQueryData<IAdminReplyList>(['adminGetReplies', page, size, reportedOnlyParam, hiddenOnlyParam]);
            if (previous) {
                const next = {
                    ...previous,
                    replies: previous.replies.map((r) => (
                        r.id === replyId
                            ? {
                                ...r,
                                status,
                                hiddenAt: status === "HIDDEN" ? (r.hiddenAt ?? new Date()) : null,
                            }
                            : r
                    )),
                };
                queryClient.setQueryData(['adminGetReplies', page, size, reportedOnlyParam, hiddenOnlyParam], next);
            }
            return { previous };
        },
        onError: (_error, _variables, context) => {
            const prev = (context as { previous?: IAdminReplyList })?.previous;
            if (prev) {
                queryClient.setQueryData(['adminGetReplies', page, size, reportedOnlyParam, hiddenOnlyParam], prev);
            }
            toast({
                title: "상태 변경에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const updateReplyHiddenReasonMutation = useMutation({
        mutationFn: adminUpdateReplyHiddenReason,
        onMutate: async (variables) => {
            const { replyId, hiddenReason } = variables;
            await queryClient.cancelQueries({ queryKey: ['adminGetReplies', page, size, reportedOnlyParam, hiddenOnlyParam] });
            const previous = queryClient.getQueryData<IAdminReplyList>(['adminGetReplies', page, size, reportedOnlyParam, hiddenOnlyParam]);
            if (previous) {
                const next = {
                    ...previous,
                    replies: previous.replies.map((r) => (
                        r.id === replyId ? { ...r, hiddenReason } : r
                    )),
                };
                queryClient.setQueryData(['adminGetReplies', page, size, reportedOnlyParam, hiddenOnlyParam], next);
            }
            return { previous };
        },
        onError: (_error, _variables, context) => {
            const prev = (context as { previous?: IAdminReplyList })?.previous;
            if (prev) {
                queryClient.setQueryData(['adminGetReplies', page, size, reportedOnlyParam, hiddenOnlyParam], prev);
            }
            toast({
                title: "숨김 사유 저장에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    type ReplyStatus = IAdminReply["status"];

    const statusDotColor = (status: ReplyStatus) => {
        if (status === "HIDDEN") return colors.apple;
        if (status === "HOLD") return colors.yellow;
        return colors.green;
    };

    const statusComponent = (replyId: string, status: ReplyStatus) => {
        return (
            <HStack justifyContent={"flex-start"} alignItems={"center"} width={"180px"} gap={2}>
                <Box
                    width={"8px"}
                    height={"8px"}
                    borderRadius={"4px"}
                    backgroundColor={statusDotColor(status)}
                />
                <Select
                    size="xs"
                    value={status}
                    onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                        const nextStatus = event.target.value as ReplyStatus;
                        if (nextStatus !== status) {
                            updateReplyStatusMutation.mutate({ replyId, status: nextStatus });
                        }
                    }}
                    isDisabled={updateReplyStatusMutation.isPending}
                >
                    <option value="NORMAL">NORMAL</option>
                    <option value="HOLD">HOLD</option>
                    <option value="HIDDEN">HIDDEN</option>
                </Select>
            </HStack>
        );
    };

    function HiddenReasonCell({ reply }: { reply: IAdminReply }) {
        const [isEditing, setIsEditing] = useState(false);
        const [value, setValue] = useState<string>(String(reply.hiddenReason ?? ""));
        const canEditHiddenReason = reply.status === "HIDDEN";

        return (
            <HStack justifyContent={"space-between"} alignItems={"center"} width={"100%"} gap={2}>
                {canEditHiddenReason && isEditing ? (
                    <>
                        <Input
                            flex={"1"}
                            minW={"320px"}
                            size={"sm"}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                        />
                        <HStack flexShrink={0} gap={1}>
                            <Button
                                size={"xs"}
                                variant={"solid"}
                                backgroundColor={colors.gray}
                                borderRadius={10}
                                _hover={{ bg: colors.blue }}
                                boxShadow="sm"
                                onClick={async () => {
                                    await updateReplyHiddenReasonMutation.mutateAsync({ replyId: reply.id, hiddenReason: value });
                                    setIsEditing(false);
                                }}
                            >
                                <Text fontSize={8} fontWeight={400} color={colors.white}>💾</Text>
                            </Button>
                            <Button
                                size={"xs"}
                                variant={"ghost"}
                                borderRadius={10}
                                onClick={() => {
                                    setValue(String(reply.hiddenReason ?? ""));
                                    setIsEditing(false);
                                }}
                            >
                                <Text fontSize={8} fontWeight={400} color={colors.black}>❌</Text>
                            </Button>
                        </HStack>
                    </>
                ) : (
                    <>
                        <Text flex={"1"} minW={"200px"} noOfLines={1} color={canEditHiddenReason ? undefined : colors.g3}>
                            {String(reply.hiddenReason ?? "-")}
                        </Text>
                        <HStack flexShrink={0} gap={1}>
                            <Button
                                size={"xs"}
                                variant={"solid"}
                                backgroundColor={colors.white}
                                borderRadius={10}
                                _hover={{ bg: canEditHiddenReason ? colors.blue : colors.white }}
                                boxShadow="sm"
                                isDisabled={!canEditHiddenReason}
                                onClick={() => {
                                    setValue(String(reply.hiddenReason ?? ""));
                                    setIsEditing(true);
                                }}
                            >
                                <Text fontSize={12} fontWeight={400} color={canEditHiddenReason ? colors.black : colors.g3}>🖋️</Text>
                            </Button>
                        </HStack>
                    </>
                )}
            </HStack>
        );
    }

    const renderCellValue = (reply: IAdminReply, columnKey: string) => {
        if (columnKey === "profile.nickname") {
            return String(reply.profile?.nickname ?? "-");
        }
        if (columnKey === "comment.profile.nickname") {
            return String(reply.comment?.profile?.nickname ?? "-");
        }
        if (columnKey === "comment.chat.profile.nickname") {
            return String(reply.comment?.chat?.profile?.nickname ?? "-");
        }
        if (columnKey === "comment.chat.content") {
            const content = reply.comment?.chat?.content ?? "-";
            const displayContent = content.length > 20 ? `${content.slice(0, 20)}...` : content;
            return (
                <Text cursor={"pointer"} onClick={() => openText(content)}>
                    {displayContent}
                </Text>
            );
        }
        if (columnKey === "comment.content") {
            const content = reply.comment?.content ?? "-";
            const displayContent = content.length > 20 ? `${content.slice(0, 20)}...` : content;
            return (
                <Text cursor={"pointer"} onClick={() => openText(content)}>
                    {displayContent}
                </Text>
            );
        }
        if (columnKey === "reports") {
            return String(reply.reports.length);
        }
        if (columnKey === "content") {
            const content = reply.content ?? "-";
            const displayContent = content.length > 20 ? `${content.slice(0, 20)}...` : content;
            return (
                <Text cursor={"pointer"} onClick={() => openText(content)}>
                    {displayContent}
                </Text>
            );
        }
        if (columnKey === "status") {
            return statusComponent(reply.id, reply.status);
        }
        if (columnKey === "hiddenReason") {
            return <HiddenReasonCell reply={reply} />;
        }
        if (columnKey === "createdAt" || columnKey === "updatedAt") {
            return formatDateTimeKor(reply[columnKey]);
        }
        return String(reply[columnKey as keyof IAdminReply] ?? "-");
    };

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>답글</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{replyListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3} />
                </HStack>

                <RadioGroup value={isReportedOnly} onChange={handleIsReportedOnlyChange}>
                    <HStack spacing={6} marginTop={4}>
                        <Box
                            paddingX={3}
                            paddingY={1}
                            backgroundColor={colors.light}
                            borderRadius={10}

                            width={"130px"}
                            textAlign={"center"}
                        >
                            <Text fontSize={12} fontWeight={500} color={colors.blue}>REPORTEDONLY</Text>
                        </Box>
                        <Radio value="all" colorScheme="blue">모두</Radio>
                        <Radio value="true" colorScheme="blue">TRUE</Radio>
                        <Radio value="false" colorScheme="blue">FALSE</Radio>
                    </HStack>
                </RadioGroup>

                <RadioGroup value={isHiddenOnly} onChange={handleIsHiddenOnlyChange}>
                    <HStack spacing={6} marginBottom={4}>
                        <Box
                            paddingX={3}
                            paddingY={1}
                            backgroundColor={colors.light}
                            borderRadius={10}

                            width={"130px"}
                            textAlign={"center"}
                        >
                            <Text fontSize={12} fontWeight={500} color={colors.blue}>HIDDENEDONLY</Text>
                        </Box>
                        <Radio value="all" colorScheme="blue">모두</Radio>
                        <Radio value="true" colorScheme="blue">TRUE</Radio>
                        <Radio value="false" colorScheme="blue">FALSE</Radio>
                    </HStack>
                </RadioGroup>

                <HStack justifyContent={"flex-start"} marginY={0}>
                    <Button
                        size={"sm"}
                        variant={"solid"}
                        isDisabled={selectedReplyIds.length === 0}
                        opacity={selectedReplyIds.length === 0 ? 0.1 : 1}
                        onClick={onOpenDeleteModal}
                    >
                        <MyIcon src={"/svg/trash-filled.svg"} alt="trash" width={16} height={16} />
                    </Button>
                    <Modal isOpen={isDeleteModalOpen} onClose={onCloseDeleteModal}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>
                                <Text fontSize={20} fontWeight={700} color={colors.black}>삭제하기</Text>
                                <ModalCloseButton />
                            </ModalHeader>
                            <ModalBody>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>선택된 행 수: {selectedReplyIds.length}</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>정말 삭제하시겠습니까?</Text>
                            </ModalBody>
                            <Stack marginTop={5} width={"100%"} padding={3}>
                                <Button
                                    onClick={onDeleteReplies}
                                    isLoading={deleteRepliesMutation.isPending}
                                    width={"100%"}
                                    backgroundColor={colors.blue}
                                    color={colors.white}
                                    borderRadius={10}
                                    _hover={{ bg: colors.blue }}
                                    boxShadow="sm"
                                >
                                    삭제
                                </Button>
                            </Stack>
                        </ModalContent>
                    </Modal>
                </HStack>

                {!isReplyListDataLoading ? (
                    <TableContainer marginTop={5}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    <Th>
                                        <Checkbox
                                            isChecked={isAllCurrentPageSelected}
                                            isIndeterminate={isSomeCurrentPageSelected}
                                            onChange={toggleSelectAllCurrentPage}
                                        />
                                    </Th>
                                    {replyColumns.map((column) => (
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {replyListData?.replies.map((reply) => (
                                    <Tr key={reply.id}>
                                        <Td>
                                            <Checkbox
                                                isChecked={selectedReplyIds.includes(reply.id)}
                                                onChange={() => toggleSelectOne(reply.id)}
                                            />
                                        </Td>
                                        {replyColumns.map((column) => (
                                            <Td key={column.key}>
                                                {renderCellValue(reply, column.key)}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Spinner color={colors.blue} />
                )}

                <HStack justifyContent={"center"} marginTop={5}>
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}

                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}

                    {pages.map((pageNumber) => (
                        <Button
                            key={pageNumber}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(pageNumber)}
                            backgroundColor={pageNumber === currentPage ? colors.blue : ""}
                            color={pageNumber === currentPage ? colors.white : ""}
                        >
                            {pageNumber}
                        </Button>
                    ))}

                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}

                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>

            <TextModal
                isOpen={isTextModalOpen}
                onClose={closeText}
                text={selectedText}
            />
        </AdminOnly>
    );
}
